// src/components/PageAccessPicker.js

import React from 'react';
import {
  PAGE_GROUPS,
  PAGE_OPTIONS,
} from '../utils/permissions';
import './PageAccessPicker.css';

const PageAccessPicker = ({ value = [], onChange }) => {
  const normalizedValue = Array.isArray(value) ? value : [];

  const selectedKeys = normalizedValue.map((key) =>
    String(key).trim().toLowerCase()
  );

  const allSelected =
    PAGE_OPTIONS.length > 0 &&
    PAGE_OPTIONS.every((page) => selectedKeys.includes(page.key));

  const selectedCount = PAGE_OPTIONS.filter((page) =>
    selectedKeys.includes(page.key)
  ).length;

  // ----------------------------------------------------------
  // TOGGLE ONE PAGE
  // ----------------------------------------------------------

  const togglePage = (pageKey) => {
    if (selectedKeys.includes(pageKey)) {
      onChange(
        normalizedValue.filter(
          (key) =>
            String(key).trim().toLowerCase() !== pageKey
        )
      );

      return;
    }

    onChange([
      ...normalizedValue,
      pageKey,
    ]);
  };

  // ----------------------------------------------------------
  // TOGGLE GROUP
  // ----------------------------------------------------------

  const toggleGroup = (group) => {
    const groupKeys = group.pages.map((page) => page.key);

    const groupFullySelected = groupKeys.every((key) =>
      selectedKeys.includes(key)
    );

    if (groupFullySelected) {
      onChange(
        normalizedValue.filter(
          (key) =>
            !groupKeys.includes(
              String(key).trim().toLowerCase()
            )
        )
      );

      return;
    }

    const merged = [...normalizedValue];

    groupKeys.forEach((key) => {
      if (!merged.some(
        (existing) =>
          String(existing).trim().toLowerCase() === key
      )) {
        merged.push(key);
      }
    });

    onChange(merged);
  };

  // ----------------------------------------------------------
  // SELECT / CLEAR ALL
  // ----------------------------------------------------------

  const toggleAll = () => {
    if (allSelected) {
      onChange([]);
      return;
    }

    onChange(PAGE_OPTIONS.map((page) => page.key));
  };

  return (
    <div className="page-access-picker">

      {/* HEADER */}
      <div className="page-access-header">

        <div>
          <div className="page-access-title">
            Page Access
          </div>

          <div className="page-access-subtitle">
            Select the pages this staff member can access.
          </div>
        </div>

        <button
          type="button"
          className="page-access-select-all"
          onClick={toggleAll}
        >
          {allSelected ? 'Clear All' : 'Select All'}
        </button>

      </div>

      {/* COUNT */}
      <div className="page-access-count">
        <span>
          {selectedCount}
        </span>

        {' '}of{' '}

        <span>
          {PAGE_OPTIONS.length}
        </span>

        {' '}pages selected
      </div>

      {/* GROUPS */}
      <div className="page-access-groups">

        {PAGE_GROUPS.map((group) => {

          const groupKeys = group.pages.map(
            (page) => page.key
          );

          const groupSelectedCount =
            groupKeys.filter((key) =>
              selectedKeys.includes(key)
            ).length;

          const groupFullySelected =
            groupSelectedCount === groupKeys.length;

          const groupPartiallySelected =
            groupSelectedCount > 0 &&
            groupSelectedCount < groupKeys.length;

          return (
            <div
              className="page-access-group"
              key={group.key}
            >

              {/* GROUP HEADER */}
              <div className="page-access-group-header">

                <div className="page-access-group-heading">

                  <span className="page-access-group-title">
                    {group.label}
                  </span>

                  <span className="page-access-group-count">
                    {groupSelectedCount}/{group.pages.length}
                  </span>

                </div>

                <button
                  type="button"
                  className="page-access-group-toggle"
                  onClick={() => toggleGroup(group)}
                >
                  {groupFullySelected
                    ? 'Clear'
                    : groupPartiallySelected
                    ? 'Select Remaining'
                    : 'Select All'}
                </button>

              </div>

              {/* PAGES */}
              <div className="page-access-grid">

                {group.pages.map((page) => {
                  const checked =
                    selectedKeys.includes(page.key);

                  return (
                    <label
                      key={page.key}
                      className={`page-access-item ${
                        checked
                          ? 'page-access-item-selected'
                          : ''
                      }`}
                    >

                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() =>
                          togglePage(page.key)
                        }
                      />

                      <span className="page-access-checkbox">
                        {checked && (
                          <span>✓</span>
                        )}
                      </span>

                      <span className="page-access-label">
                        {page.label}
                      </span>

                    </label>
                  );
                })}

              </div>

            </div>
          );
        })}

      </div>

      {/* PROFILE INFO */}
      <div className="page-access-profile-note">
        <span className="page-access-info-icon">
          i
        </span>

        <span>
          Profile is always available to the staff member and
          does not need to be selected.
        </span>
      </div>

      {/* SHOP INFO */}
      <div className="page-access-shop-note">
        <span className="page-access-info-icon">
          i
        </span>

        <span>
          Staff members can only access the shop assigned to
          their account. Shop management is available only to
          Super Admin.
        </span>
      </div>

    </div>
  );
};

export default PageAccessPicker;